import React, { useEffect, useMemo, useState } from 'react';
import './help.css';
import {
  createSupportRequest,
  getAllSupportRequests,
  dismissSupportResponse,
  subscribeToSupportRequestChanges,
} from '../../modules/supportRequestStorage';

const FAQS = [
  {
    question: 'How do I craft prompts that consistently deliver?',
    answer:
      'Start with subject + mood + medium + detail level, then keep a library of “winning” phrasing. Use prompt history to clone and iterate.',
  },
  {
    question: 'Why was my generation flagged or rejected?',
    answer:
      'We automatically block disallowed content, sensitive identities, or low-confidence prompts. Review moderation guidelines and resubmit with adjustments.',
  },
  {
    question: 'Where do I review my credit plan details?',
    answer:
      'Open Account → Credit Plan to see your current plan, renewal cadence, and billing contacts.',
  },
  {
    question: 'Can I invite clients to view collections?',
    answer:
      'Yes. Share a secure viewer link or add them as guests with comment-only access so they can leave feedback without editing the catalog.',
  },
  {
    question: 'How do I report a rendering bug?',
    answer:
      'Use the in-product “Report an issue” button within the AI studio, attach logs/screenshots, or email support@soalive.net with the history ID.',
  },
];

const INITIAL_SUPPORT_FORM = {
  name: '',
  email: '',
  subject: '',
  message: '',
};

const Help = () => {
  const [supportForm, setSupportForm] = useState(INITIAL_SUPPORT_FORM);
  const [supportStatus, setSupportStatus] = useState(null);
  const [supportError, setSupportError] = useState(null);
  const [requests, setRequests] = useState(getAllSupportRequests);

  const handleSupportChange = (event) => {
    const { name, value } = event.target;
    setSupportForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSupportSubmit = (event) => {
    event.preventDefault();
    setSupportStatus(null);
    setSupportError(null);
    try {
      createSupportRequest(supportForm);
      setSupportStatus('Thanks! Your message was sent to the admin team.');
      setSupportForm(INITIAL_SUPPORT_FORM);
    } catch (error) {
      setSupportError(error.message || 'Unable to send your message right now.');
    }
  };

  useEffect(() => {
    const unsubscribe = subscribeToSupportRequestChanges((entries) => {
      setRequests(entries);
    });
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const latestResponse = useMemo(() => {
    if (!Array.isArray(requests) || !requests.length) {
      return null;
    }
    return (
      [...requests]
        .filter((entry) => entry.status === 'responded' && !entry.dismissedAt && entry.response)
        .sort((a, b) => new Date(b.respondedAt || 0) - new Date(a.respondedAt || 0))[0] || null
    );
  }, [requests]);

  const handleDismissResponse = () => {
    if (latestResponse) {
      dismissSupportResponse(latestResponse.id);
    }
  };

  const handleRespondAgain = () => {
    if (latestResponse) {
      setSupportForm((previous) => ({
        ...previous,
        subject: `Re: ${latestResponse.subject}`,
      }));
      const target = document.querySelector('#contact-support');
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <section className="help-center">
      {latestResponse && (
        <div className="help-response-banner">
          <div>
            <strong>Update: Admin responded at {new Date(latestResponse.respondedAt).toLocaleString()}</strong>
            <p>{latestResponse.response}</p>
          </div>
          <div className="help-response-banner__actions">
            <button type="button" className="help-button help-button--secondary" onClick={() => {
              const modal = document.getElementById('help-transcript');
              if (modal) {
                modal.showModal();
              }
            }}>
              View Transcript
            </button>
            <button type="button" className="help-button help-button--ghost" onClick={handleRespondAgain}>
              Respond
            </button>
            <button type="button" className="help-button help-button--secondary" onClick={handleDismissResponse}>
              Dismiss
            </button>
          </div>
        </div>
      )}
      {latestResponse && (
        <dialog id="help-transcript" className="help-transcript">
          <div className="help-transcript__content">
            <h3>Conversation Transcript</h3>
            <div className="help-transcript__list">
              {Array.isArray(latestResponse.transcript) &&
                [...latestResponse.transcript]
                  .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))
                  .map((item, index) => (
                    <article key={`${item.at}-${index}`} className="help-transcript__entry">
                      <header>
                        <span>{item.from === 'admin' ? 'Admin' : 'You'}</span>
                        <time>{new Date(item.at).toLocaleString()}</time>
                      </header>
                      <p>{item.body}</p>
                    </article>
                  ))}
            </div>
            <div className="help-transcript__actions">
              <button
                type="button"
                className="help-button help-button--secondary"
                onClick={() => document.getElementById('help-transcript').close()}
              >
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}
      <header className="help-hero">
        <div>
          <p className="help-hero__eyebrow">Support & Guidance</p>
          <h1>We’re Here to Keep Your Creative Ops in Flow</h1>
          <p>
            Browse curated playbooks, troubleshoot AI workflows, and connect with experts who know
            your account inside and out.
          </p>
          <div className="help-hero__actions">
            <a href="#faqs" className="help-button help-button--primary">
              Search Documentation
            </a>
          </div>
        </div>
        <figure className="help-hero__visual" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1000&q=80"
            alt=""
            loading="lazy"
          />
          <figcaption>24/7 experts, curated guides, and proactive monitoring.</figcaption>
        </figure>
      </header>

      <section className="help-cta">
        <div className="help-cta__content">
          <p>New to soalive.net?</p>
          <h3>Follow the onboarding runway to launch your first campaign in under an hour.</h3>
          <ol>
            <li>Set up your workspace defaults and creative preferences.</li>
            <li>Build your first AI mood board with curated prompts.</li>
            <li>Export creative deliverables and capture stakeholder feedback.</li>
          </ol>
        </div>
        <div className="help-cta__actions">
          <a href="/account#onboarding" className="help-button help-button--primary">
            Start Onboarding
          </a>
        </div>
      </section>

      <section className="help-faq" aria-labelledby="faqs">
        <div className="help-faq__header">
          <h2 id="faqs">Frequently Asked Questions</h2>
          <p>Answers to the questions we hear the most from creators and admins.</p>
        </div>
        <dl>
          {FAQS.map((faq) => (
            <div key={faq.question} className="help-faq__item">
              <dt>{faq.question}</dt>
              <dd>{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="help-status" id="status-feed">
        <h2>Status &amp; Uptime</h2>
        <p>Check live component health, response times, and incident logs any time.</p>
        <a href="/status" className="help-button help-button--secondary">
          Open status page
        </a>
      </section>

      <section className="help-contact" id="contact-support">
        <div>
          <h2>Need a Human?</h2>
          <p>Submit the form and our admin team will follow up via email.</p>
        </div>
        <form className="help-contact__form" onSubmit={handleSupportSubmit}>
          <label>
            Name
            <input
              type="text"
              name="name"
              value={supportForm.name}
              onChange={handleSupportChange}
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              name="email"
              value={supportForm.email}
              onChange={handleSupportChange}
              required
            />
          </label>
          <label>
            Subject
            <input
              type="text"
              name="subject"
              value={supportForm.subject}
              onChange={handleSupportChange}
              required
            />
          </label>
          <label>
            Message
            <textarea
              name="message"
              rows="4"
              value={supportForm.message}
              onChange={handleSupportChange}
              required
            />
          </label>
          <button type="submit" className="help-button help-button--primary">
            Send Message
          </button>
          {supportStatus && <p className="help-contact__status">{supportStatus}</p>}
          {supportError && (
            <p className="help-contact__status help-contact__status--error">{supportError}</p>
          )}
        </form>
      </section>
    </section>
  );
};

export default Help;
